import request, { apiURL } from './api';
import {
  formatCurrency as formatCurrencyLibrary,
  formatNumberToString,
  formatDateDisplayLocal,
  getCSVFromTableRows,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import { download } from '../utils/downloadFile';
import { getCookie } from './cookie';

export const getCSV = async ({
  organizationId,
  selectedPropertyId,
  key,
  query,
}: {
  organizationId: string,
  selectedPropertyId: string,
  key: string,
  query: string,
}) => {
  const options = {
    headers: {
      Authorization: getCookie('session_id'),
      'Content-Type': 'application/json',
    },
  };

  return request(
    `${apiURL}/${organizationId}/${selectedPropertyId}/${key}${
      query?.length ? `?${query}` : ''
    }`,
    options,
  );
};

export const getHeaders = (headers: any, removeHeaderId: string) => {
  return headers.filter((header) => header.id !== removeHeaderId);
};

export const formatCurrency = (num) =>
  `"${Number(num).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })}"`;

export const appendFilterTextToCSV = ({ headers, rows, hasAnyFilters }) => {
  const csvRows = Array.isArray(rows) ? rows.join('\n') : rows ?? '';
  let csvData = [headers];
  if (csvRows.length) {
    csvData.push(csvRows);
  }
  // Prepends filter text if filters are present
  if (hasAnyFilters) {
    const columnCount = headers.split(',').length;
    const emptyColumns = ','.repeat(Math.max(columnCount - 1, 0));
    csvData = [
      `"This report was filtered prior to being generated"${emptyColumns}`,
      emptyColumns,
      ...csvData,
    ];
  }
  csvData = csvData.join('\n');

  return csvData;
};

// Variants handled specially by the component library - do not wrap them as
// Excel text formulas here, but still normalize/sanitize/escape them for valid CSV output.
const EXCEL_SAFE_VARIANTS = new Set([
  'date',
  'dateTime',
  'dateUTC',
  'dateTimeUTC',
  'currency',
  'number',
  'boolean',
  'icon',
  'menu',
  'iconButtons',
  'texting',
  'phoneNumber',
  'string',
  'description',
]);

const MULTILINE_SAFE_VARIANTS = new Set(['icon']);
const EXCEL_FORMULA_PREFIX_RE = /^[=+\-@]/;

// Normalizes whitespace while optionally preserving real line breaks for CSV cells
const normalizeCsvCellString = (
  value: ?string,
  preserveLineBreaks: boolean = false,
): ?string => {
  if (typeof value !== 'string') return value;

  const normalizedValue = value.replace(/\t/g, ' ');
  if (preserveLineBreaks) {
    return normalizedValue.replace(/\r\n?|\u2028|\u2029/g, '\n');
  }

  return normalizedValue.replace(/\s*[\r\n\u2028\u2029]+\s*/g, ' | ');
};

// Safe variants skip ="..." wrapping, so guard against CSV/Excel formula injection.
const sanitizeExcelSafeString = (value: ?string): ?string => {
  if (
    typeof value !== 'string' ||
    !value.length ||
    value.startsWith("'") ||
    !EXCEL_FORMULA_PREFIX_RE.test(value)
  ) {
    return value;
  }

  return `'${value}`;
};

// Escapes double quotes for the component library CSV formatter.
// If there are no commas, we quote the full field ourselves because the
// component library only auto-quotes strings containing commas.
const escapeCsvStringForComponentLibrary = (value: ?string): ?string => {
  if (typeof value !== 'string') return value;

  const needsFullCsvQuoting =
    value.includes('"') || /[\r\n\u2028\u2029]/.test(value);
  if (!needsFullCsvQuoting) return value;
  const escapedValue = value.replace(/"/g, '""');
  return value.includes(',') ? escapedValue : `"${escapedValue}"`;
};

// Wraps a string value as an Excel text formula to prevent auto-formatting
export const excelEscapeString = (value: ?string): ?string => {
  if (!value || value.startsWith('="')) return value;
  if (value.includes(',')) {
    return escapeCsvStringForComponentLibrary(value);
  }
  const escapedValue = value.replace(/"/g, '""');
  return `="${escapedValue}"`;
};

// Pre-processes rows so string values are not auto-formatted by Excel
const preProcessRowsForExcel = (rows) =>
  rows.map((row) => {
    const processedRow = {};
    Object.keys(row).forEach((key) => {
      const rawCell = row[key];
      const cell =
        rawCell &&
        typeof rawCell === 'object' &&
        typeof rawCell.value === 'string'
          ? (() => {
              const normalizedValue = normalizeCsvCellString(
                rawCell.value,
                MULTILINE_SAFE_VARIANTS.has(rawCell.variant),
              );
              const safeValue = EXCEL_SAFE_VARIANTS.has(rawCell.variant)
                ? sanitizeExcelSafeString(normalizedValue)
                : normalizedValue;

              return {
                ...rawCell,
                value: EXCEL_SAFE_VARIANTS.has(rawCell.variant)
                  ? escapeCsvStringForComponentLibrary(safeValue)
                  : safeValue,
              };
            })()
          : rawCell;
      if (
        !cell ||
        typeof cell !== 'object' ||
        EXCEL_SAFE_VARIANTS.has(cell.variant) ||
        typeof cell.value !== 'string'
      ) {
        processedRow[key] = cell;
      } else {
        processedRow[key] = { ...cell, value: excelEscapeString(cell.value) };
      }
    });
    return processedRow;
  });

const ensureCsvFileName = (fileName: string) => {
  if (!fileName) return 'download.csv';
  return fileName.toLowerCase().endsWith('.csv') ? fileName : `${fileName}.csv`;
};

const prependUtf8Bom = (csv: string) => {
  if (!csv || csv.startsWith('\uFEFF')) return csv;
  return `\uFEFF${csv}`;
};

export const downloadCsvWithUtf8Bom = (
  csv: string,
  fileName: string,
  mimeType: string = 'text/csv;charset=utf-8',
) => {
  download(prependUtf8Bom(csv), ensureCsvFileName(fileName), mimeType);
};

const getHeaderLabel = (label) =>
  typeof label === 'string' ? label : label?.props?.defaultMessage ?? '';

export const buildTableRowsCSVBody = ({
  excludedHeaders = [],
  filteredHeaders = [],
  rows = [],
}: {
  excludedHeaders?: string[],
  filteredHeaders: {
    id: string,
    label: string | { props: { defaultMessage: string } },
  }[],
  rows: { [headerId: string]: { variant: string, value: string | number } }[],
}) => {
  const csvWithoutFilterText = getCSVFromTableRows({
    excludedHeaders,
    filteredHeaders,
    hasAnyFilters: false,
    rows: preProcessRowsForExcel(rows),
  });

  const headers = filteredHeaders
    .filter(({ id }) => !excludedHeaders.includes(id))
    .map(({ label }) => getHeaderLabel(label))
    .join(',');

  if (!headers) {
    return csvWithoutFilterText ?? '';
  }

  if (
    typeof csvWithoutFilterText === 'string' &&
    csvWithoutFilterText.startsWith(`${headers}\n`)
  ) {
    return csvWithoutFilterText.slice(headers.length + 1);
  }

  return '';
};

// This is a dynamic function, meant to be used with Fortress Table from CL
export const exportAndDownloadCSV = ({
  excludedHeaders = [],
  filteredHeaders = [],
  fileName = '',
  hasAnyFilters = false,
  rows = [],
  appendixHeaders = null,
  appendixRows = null,
}: {
  excludedHeaders?: string[],
  filteredHeaders: {
    id: string,
    label: string | { props: { defaultMessage: string } },
  }[],
  fileName: string,
  hasAnyFilters: boolean,
  rows: { [headerId: string]: { variant: string, value: string | number } }[],
  appendixHeaders?: ?Array<{
    id: string,
    label: string | { props: { defaultMessage: string } },
  }>,
  appendixRows?: ?Array<{
    [headerId: string]: { variant: string, value: string | number },
  }>,
}) => {
  const headers = filteredHeaders
    .filter(({ id }) => !excludedHeaders.includes(id))
    .map(({ label }) => getHeaderLabel(label))
    .join(',');

  let csvRows = buildTableRowsCSVBody({
    excludedHeaders,
    filteredHeaders,
    rows,
  });

  if (appendixHeaders?.length && appendixRows?.length) {
    const appendixBody = buildTableRowsCSVBody({
      excludedHeaders: [],
      filteredHeaders: appendixHeaders,
      rows: appendixRows,
    });
    csvRows = csvRows.length ? `${csvRows}\n${appendixBody}` : appendixBody;
  }

  const csv = appendFilterTextToCSV({
    headers,
    rows: csvRows,
    hasAnyFilters,
  });

  downloadCsvWithUtf8Bom(csv, fileName);
};

export const removeCommasForCSVExport = (csvDataArray: string[]) => {
  return csvDataArray.map((value) => {
    if (typeof value === 'string' && value.includes(',')) {
      value = value.split(',').join('');
    }
    return value;
  });
};

// DO NOT USE! USE [exportAndDownloadCSV]
export const processDataToCSV = ({ rows, filteredHeaders }) => {
  const selectFormat = ({ variant, value }, id) => {
    if (value === null || value === undefined) return '';
    if (id === 'sqFeet') variant = 'number';
    if (id === 'requestor') variant = 'list';
    if (!variant) return `="${value}"` ?? '';
    let dataTypes = {
      link: () => value ?? '',
      description: () => value ?? '',
      currency: () => formatCurrencyLibrary(value),
      number: () => formatNumberToString(value),
      string: () => value ?? '',
      date: () => formatDateDisplayLocal(value) ?? value,
      dateTime: () => formatDateDisplayLocal(value) ?? value,
      status: () => value ?? '',
      priority: () => value ?? '',
      list: () =>
        value.filter((valor) => valor !== null && valor !== '').join(' ') ?? '',
    };
    return dataTypes[variant] ? `"${dataTypes[variant]()}"` : '';
  };

  return rows.map((floorPlan) => {
    const csvRow = filteredHeaders.map(({ id }) =>
      selectFormat(floorPlan[id], id),
    );
    return csvRow.join(',');
  });
};
